// NEW_FILE_JUSTIFIED: Read-only audit log viewer for the type builder
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official Convex React client package, not a cross-boundary import from the local convex/ directory
import { api } from '@convex/_generated/api';
import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, History } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { IHistoryTabProps, IDiffLineProps } from '@/@types/insuranceBuilder';

export function HistoryTab({ typeId }: IHistoryTabProps) {
  const history = useQuery(api['insurance/typeHistory'].listChangeHistory, { typeId });

  if (history === undefined) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <History className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">No edits yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map(entry => (
        <Card key={entry._id}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">v{entry.version}</Badge>
              <span className="text-sm font-medium">{entry.changedByName ?? 'Unknown user'}</span>
              <span className="text-xs text-muted-foreground">
                · {formatDistanceToNow(entry.changedAt, { addSuffix: true })}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{entry.summary}</p>
          </CardHeader>
          {entry.changes.length > 0 && (
            <CardContent>
              <Collapsible>
                <CollapsibleTrigger className="text-xs text-muted-foreground flex items-center gap-1 hover:underline">
                  <ChevronDown className="w-3 h-3" />
                  Show {entry.changes.length} change{entry.changes.length > 1 ? 's' : ''}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-1">
                  {entry.changes.map((c, idx) => (
                    <DiffLine key={idx} change={c} />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

function DiffLine({ change }: IDiffLineProps) {
  const opLabel: Record<string, string> = { add: '+', remove: '−', update: '~', reorder: '⇅' };
  const opColor: Record<string, string> = {
    add: 'text-emerald-600',
    remove: 'text-rose-600',
    update: 'text-amber-600',
    reorder: 'text-blue-600',
  };

  return (
    <div className="flex items-baseline gap-2 text-xs font-mono">
      <span className={cn(opColor[change.op])}>{opLabel[change.op] ?? '?'}</span>
      <span className="capitalize text-muted-foreground">{change.target}</span>
      <span className="font-sans">{change.label ?? change.targetKey ?? ''}</span>
      {change.before !== undefined && change.after !== undefined && (
        <span className="text-muted-foreground">
          {String(change.before)} → {String(change.after)}
        </span>
      )}
    </div>
  );
}
